import React, { useState } from 'react';
import { AppNode, Edge, SimulationState } from '../types';
import { determineNextNode, evaluateNode } from '../services/SimulationStrategies';
import { useToast } from '../contexts/ToastContext';

interface UseWorkflowSimulationProps {
    nodes: AppNode[];
    setNodes: React.Dispatch<React.SetStateAction<AppNode[]>>;
    edges: Edge[];
    fitView: (options?: any) => void;
    focusNode: (nodeId: string) => void;
}

export const useWorkflowSimulation = ({
    nodes,
    setNodes,
    edges,
    fitView,
    focusNode
}: UseWorkflowSimulationProps) => {
    
  const { addToast } = useToast();
  
  const [simulationState, setSimulationState] = useState<SimulationState>({
    isRunning: false,
    currentNodeId: null,
    isWaitingForInput: false,
    logs: [],
    variables: {},
    history: [],
    executionQueue: [],
    visitedEdgeIds: []
  });

  const [suggestedPath, setSuggestedPath] = useState<string | null>(null);

  const addLog = (message: string) => {
      setSimulationState(prev => ({
          ...prev,
          logs: [...prev.logs, message]
      }));
  };

  const startSimulation = () => {
      // Reset visuals
      setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, status: 'idle' } })));
      
      const startNodes = nodes.filter(n => n.type?.endsWith('StartEvent'));
      
      if (startNodes.length === 0) {
          addToast({ type: 'error', title: 'Simulation Failed', message: "No Start Event found. Please add a Start Event to the workflow." });
          return;
      }

      setSimulationState({
          isRunning: true,
          currentNodeId: null, 
          isWaitingForInput: false,
          logs: ["Simulation initialized. Waiting for start selection..."],
          variables: {},
          history: [],
          executionQueue: [],
          visitedEdgeIds: []
      });

      addToast({ type: 'info', title: 'Simulation Mode', message: 'Select a start event to begin.' });
      setTimeout(() => fitView({ duration: 800 }), 100);

      // Auto-start if only one start event
      if (startNodes.length === 1) {
          runSimulationStep(startNodes[0].id);
      }
  };

  const stopSimulation = () => {
      setSimulationState(prev => ({ 
          ...prev, 
          isRunning: false, 
          currentNodeId: null,
          isWaitingForInput: false,
          executionQueue: []
      }));
      setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, status: 'idle' } })));
      setTimeout(() => fitView({ duration: 800 }), 100);
  };

  const resetSimulation = () => {
      setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, status: 'idle' } })));
      setSimulationState(prev => ({...prev, logs: [], variables: {}, history: [], executionQueue: [], visitedEdgeIds: []}));
      addLog("Simulation state reset.");
  };

  const runSimulationStep = (nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return;

      setSimulationState(prev => ({
          ...prev,
          history: [...prev.history, { 
              nodeId: prev.currentNodeId || 'root', 
              variables: JSON.parse(JSON.stringify(prev.variables)),
              timestamp: Date.now()
          }],
          currentNodeId: nodeId,
          isWaitingForInput: true
      }));

      // Update Visuals
      setNodes(nds => nds.map(n => n.id === nodeId ? ({...n, data: {...n.data, status: 'active'}}) : n));
      
      setTimeout(() => focusNode(nodeId), 100);
      
      addLog(`Entering node: ${node.data.label}`);

      // Evaluate logic immediately for suggestions
      const suggestion = evaluateNode(node, simulationState.variables);
      setSuggestedPath(suggestion);
  };

  // Switch to the next item in queue without completing the current node (used for blocking merges)
  const processQueue = () => {
      setSimulationState(prev => {
          if (prev.executionQueue.length === 0) return prev;
          
          const nextId = prev.executionQueue[0];
          const remainingQueue = prev.executionQueue.slice(1);

          // Mark current node as waiting instead of active/completed if we are yielding
          setNodes(nds => nds.map(n => n.id === prev.currentNodeId ? ({...n, data: {...n.data, status: 'waiting'}}) : n));

          setTimeout(() => runSimulationStep(nextId), 50);

          return {
              ...prev,
              executionQueue: remainingQueue
          };
      });
  };

  const handleSimulationAction = (action: 'NEXT' | 'BACK' | 'STOP' | 'SELECT_START' | 'PROCESS_QUEUE', data?: any) => {
      if (action === 'STOP') {
          stopSimulation();
          return;
      }

      if (action === 'SELECT_START') {
          runSimulationStep(data.nodeId);
          return;
      }

      if (action === 'PROCESS_QUEUE') {
          processQueue();
          return;
      }

      if (action === 'BACK') {
          if (simulationState.history.length === 0) return;
          const previousFrame = simulationState.history[simulationState.history.length - 1];
          const newHistory = simulationState.history.slice(0, -1);
          
          // Note: Back travel in multi-path simulation is complex. 
          // Simplified: We just jump back state, but don't undo queue/edge visits deeply.
          setSimulationState(prev => ({
              ...prev,
              currentNodeId: previousFrame.nodeId === 'root' ? null : previousFrame.nodeId, 
              variables: previousFrame.variables,
              history: newHistory,
              isWaitingForInput: previousFrame.nodeId !== 'root' 
          }));

          if (previousFrame.nodeId !== 'root') {
               setNodes(nds => nds.map(n => n.id === previousFrame.nodeId ? ({...n, data: {...n.data, status: 'active'}}) : ({...n, data: {...n.data, status: 'idle'}})));
               setTimeout(() => focusNode(previousFrame.nodeId), 100);
          } else {
              setNodes(nds => nds.map(n => ({...n, data: {...n.data, status: 'idle'}})));
              setTimeout(() => fitView({ duration: 800 }), 100);
          }
          return;
      }

      if (action === 'NEXT') {
          const currentNodeId = simulationState.currentNodeId;
          if (!currentNodeId) return;

          const node = nodes.find(n => n.id === currentNodeId);
          if (!node) return;

          const updatedVariables = { ...simulationState.variables, ...data };
          // Remove internal simulation flags from variables if any
          delete updatedVariables.selectedPath; 

          // Mark current as completed
          setNodes(nds => nds.map(n => n.id === currentNodeId ? ({...n, data: {...n.data, status: 'completed'}}) : n));
          
          // Determine next step(s)
          const result = determineNextNode(node, edges, data, updatedVariables);
          
          result.logs.forEach(log => addLog(log));

          // Update traversed edges
          const newVisitedEdges = [...simulationState.visitedEdgeIds, ...result.traversedEdgeIds];

          // Handle next nodes logic (Queueing for Splits)
          const nextNodes = result.nextNodes;

          if (nextNodes.length === 0) {
              // Path ended. Check queue.
              if (simulationState.executionQueue.length > 0) {
                  const nextFromQueue = simulationState.executionQueue[0];
                  const remainingQueue = simulationState.executionQueue.slice(1);
                  
                  setSimulationState(prev => ({
                      ...prev,
                      variables: updatedVariables,
                      visitedEdgeIds: newVisitedEdges,
                      executionQueue: remainingQueue
                  }));
                  
                  addLog("Path completed. Switching to pending parallel branch.");
                  setTimeout(() => runSimulationStep(nextFromQueue), 50);
              } else {
                  // Truly finished
                  setSimulationState(prev => ({
                      ...prev,
                      variables: updatedVariables,
                      visitedEdgeIds: newVisitedEdges
                  }));
                  addLog("Workflow execution completed.");
                  addToast({ type: 'success', title: 'Completed', message: 'Workflow execution finished successfully.' });
              }
          } else {
              // We have next node(s)
              const primaryNext = nextNodes[0];
              const additionalNext = nextNodes.slice(1);

              setSimulationState(prev => ({
                  ...prev,
                  variables: updatedVariables,
                  visitedEdgeIds: newVisitedEdges,
                  executionQueue: [...prev.executionQueue, ...additionalNext]
              }));

              if (additionalNext.length > 0) {
                  addLog(`Queued ${additionalNext.length} parallel branches.`);
              }

              runSimulationStep(primaryNext);
          }
      }
  };

  return {
      simulationState,
      suggestedPath,
      startSimulation,
      stopSimulation,
      resetSimulation,
      handleSimulationAction
  };
}